import { Capacitor } from '@capacitor/core';

export const isNativeApp = () => Capacitor.isNativePlatform();

// Only route trusted Bella Tracker links into the local application.
export function nativeLinkPath(value: string): string | null {
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:' || url.hostname !== 'bellatracker.com' || url.port || url.username || url.password) return null;
    if (!['/invite', '/signin', '/signup', '/app', '/timer', '/delete-account'].includes(url.pathname)) return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch { return null; }
}

export function initializeNativeApp() {
  if (!isNativeApp()) return;
  if (location.pathname === '/') history.replaceState({}, '', '/app');
  void import('@capacitor/app').then(async ({ App }) => {
    const open = (url: string) => {
      const path = nativeLinkPath(url);
      if (!path) return;
      history.pushState({}, '', path);
      window.dispatchEvent(new PopStateEvent('popstate'));
    };
    await App.addListener('appUrlOpen', ({ url }) => open(url));
    const launch = await App.getLaunchUrl();
    if (launch) open(launch.url);
    await App.addListener('backButton', () => {
      // Let the modal's existing unsaved-change and deletion guards decide.
      if (document.querySelector('[role="dialog"]')) {
        document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', cancelable: true }));
      } else if (location.pathname !== '/app' && location.pathname !== '/timer') {
        history.replaceState({}, '', '/app');
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    });
  }).catch(error => console.error('Android navigation could not initialize', error));
  document.addEventListener('click', event => {
    const anchor = (event.target as Element | null)?.closest<HTMLAnchorElement>('a[href]');
    if (!anchor || anchor.download || event.defaultPrevented) return;
    const url = new URL(anchor.href, location.href);
    if (url.protocol === 'https:' && url.origin !== location.origin) {
      event.preventDefault();
      void import('@capacitor/browser').then(({ Browser }) => Browser.open({ url: url.href }));
    }
  });
}

export async function saveFile(blob: Blob, name: string): Promise<void> {
  if (!isNativeApp()) {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url; anchor.download = name; anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return;
  }
  const [{ Filesystem, Directory }, { Share }] = await Promise.all([
    import('@capacitor/filesystem'), import('@capacitor/share'),
  ]);
  const data = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('The file could not be prepared.'));
    reader.onload = () => resolve(String(reader.result).split(',')[1]);
    reader.readAsDataURL(blob);
  });
  const path = `exports/${crypto.randomUUID()}/${name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
  const file = await Filesystem.writeFile({ path, data, directory: Directory.Cache, recursive: true });
  // The receiving app may read asynchronously after the share sheet closes.
  // Keep this in the private, OS-managed cache rather than deleting it too early.
  await Share.share({ title: name, files: [file.uri], dialogTitle: 'Save or share your record' });
}
